import { useState, type ComponentType, type SVGProps } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { usePracticePatientInsights } from '../../../../hooks/usePracticePatientInsights';
import type {
    PatientInsightsGranularity,
    PracticePatientInsightsBucket
} from '../../../../services/practicePatientInsights';
import { patientInsightsWindowLabel } from '../../../../services/practicePatientInsights';
import {
    AXIS_TICK,
    CHART_RESIZE_DEBOUNCE_MS,
    HOVER_CURSOR,
    formatBucketLabel
} from '../../../../utils/chartFormatting';
import { CardErrorState } from '../../overview/CardErrorState';
import { PatientInsightsGranularityToggle } from './PatientInsightsGranularityToggle';

// Generic single-series bar card shared by SubmissionsInsightCard and
// ApprovalsInsightCard - same card shell (header/skeleton/empty/error) and
// chart shape as billing's MonthlyCollectedChart, parameterized by which
// field of the shared patient-insights series it plots. Owns its own
// granularity state and query, same self-contained shape as CommunicationsOverTimeCard.
const CARD_CLASS = 'bg-white rounded-xl border border-gray-200 shadow-sm';
const CHART_HEIGHT = 180;

interface ChartRow {
    label: string;
    value: number;
}

interface InsightTooltipProps {
    active?: boolean;
    label?: string;
    payload?: { value?: number }[];
    metricLabel: string;
    color: string;
}

function InsightTooltip({ active, label, payload, metricLabel, color }: InsightTooltipProps) {
    if (!active || !payload || payload.length === 0) return null;
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3.5 py-2.5 min-w-[140px]">
            <p className="text-xs font-bold text-gray-800 mb-1.5">{label}</p>
            <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-[2px] shrink-0" style={{ background: color }} />
                <span className="text-[11px] text-gray-500 flex-1">{metricLabel}</span>
                <span className="text-[11px] font-semibold text-gray-900 tabular-nums">
                    {payload[0].value ?? 0}
                </span>
            </div>
        </div>
    );
}

function ChartSkeleton() {
    return (
        <div className="flex items-end gap-2 h-full px-1">
            {Array.from({ length: 12 }).map((_, i) => (
                <div
                    key={i}
                    className="flex-1 rounded-t bg-gray-100 animate-pulse"
                    style={{ height: `${30 + ((i * 13) % 60)}%` }}
                />
            ))}
        </div>
    );
}

interface PatientInsightsCardProps {
    practiceId: string;
    metricLabel: string;
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    color: string;
    metric: keyof Pick<PracticePatientInsightsBucket, 'submissions' | 'approvals'>;
}

export function PatientInsightsCard({
    practiceId,
    metricLabel,
    icon: Icon,
    color,
    metric
}: PatientInsightsCardProps) {
    const [granularity, setGranularity] = useState<PatientInsightsGranularity>('month');
    const { buckets, isLoading, isError, refetch } = usePracticePatientInsights(
        practiceId,
        granularity
    );
    const rows: ChartRow[] = buckets.map((point) => ({
        label: formatBucketLabel(point.bucket, granularity),
        value: point[metric]
    }));
    const hasData = rows.some((row) => row.value > 0);
    const windowLabel = patientInsightsWindowLabel(granularity);

    return (
        <div className={CARD_CLASS}>
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 shrink-0">
                <div className="min-w-0">
                    <h3 className="text-[15px] font-bold text-gray-800">{metricLabel}</h3>
                    <span className="text-[11px] text-gray-400">{windowLabel}</span>
                </div>
                <PatientInsightsGranularityToggle value={granularity} onChange={setGranularity} />
            </div>
            <div className="px-5 py-4" style={{ height: CHART_HEIGHT }}>
                {isLoading ? (
                    <ChartSkeleton />
                ) : isError ? (
                    <CardErrorState
                        message={`Couldn't load ${metricLabel.toLowerCase()}`}
                        onRetry={() => void refetch()}
                    />
                ) : !hasData ? (
                    <div className="h-full flex flex-col items-center justify-center gap-2 text-center">
                        <Icon className="h-8 w-8 text-gray-300" strokeWidth={1} />
                        <p className="text-sm text-gray-400">
                            No {metricLabel.toLowerCase()} in the {windowLabel.toLowerCase()}.
                        </p>
                    </div>
                ) : (
                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                        debounce={CHART_RESIZE_DEBOUNCE_MS}
                        className="overflow-x-clip"
                    >
                        <BarChart
                            data={rows}
                            margin={{ top: 18, right: 4, left: -8, bottom: 0 }}
                            barCategoryGap="24%"
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#f3f4f6"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="label"
                                tick={AXIS_TICK}
                                axisLine={false}
                                tickLine={false}
                                tickMargin={6}
                            />
                            <YAxis
                                tick={AXIS_TICK}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                            />
                            <Tooltip
                                cursor={HOVER_CURSOR}
                                content={<InsightTooltip metricLabel={metricLabel} color={color} />}
                            />
                            <Bar
                                dataKey="value"
                                name={metricLabel}
                                fill={color}
                                radius={[3, 3, 0, 0]}
                                maxBarSize={52}
                            >
                                <LabelList
                                    dataKey="value"
                                    position="top"
                                    formatter={(value) => (Number(value) > 0 ? String(value) : '')}
                                    style={{ fontSize: 10, fontWeight: 600, fill: '#6b7280' }}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}
